import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { useLanguage } from '../../i18n/LanguageContext';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const FactoryContainer = styled.div`
  padding: 140px 0;
  background: #ffffff;
  min-height: 100vh;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 5%;
`;

const PageTitle = styled.h1`
  text-align: center;
  font-size: 4.5rem;
  color: #2c2c2c;
  margin-bottom: 30px;
  
  letter-spacing: -2px;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 4px;
    background: linear-gradient(90deg, #dc2626, #ef4444);
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    font-size: 3rem;
  }
`;

const PageSubtitle = styled.p`
  text-align: center;
  font-size: 1.4rem;
  color: #6c757d;
  margin-bottom: 120px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.7;
  
`;

// Factory Info Section
const FactoryInfoSection = styled.section`
  margin-bottom: 120px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: center;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 50px;
  }
  
  &.animate-in {
    .info-content { animation: ${fadeInUp} 0.8s ease-out; }
    .info-images { animation: ${scaleIn} 0.8s ease-out 0.2s both; }
  }
`;

const InfoContent = styled.div`
  h2 {
    font-size: 3rem;
    
    color: #2c2c2c;
    margin-bottom: 30px;
    letter-spacing: -1px;
  }
  
  p {
    font-size: 1.2rem;
    line-height: 1.8;
    color: #374151;
    margin-bottom: 25px;
    
  }
  
  .highlight {
    background: linear-gradient(135deg, #dc2626, #ef4444);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    font-weight: 500;
  }
`;

const InfoImages = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
`;

const ImageCard = styled.div`
  position: relative;
  height: 200px;
  border-radius: 16px;
  overflow: hidden;
  background: #f8fafc;
  border: 2px dashed #cbd5e1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  }
  
  .placeholder {
    font-size: 3rem;
    color: #94a3b8;
    margin-bottom: 10px;
  }
  
  .caption {
    color: #64748b;
    font-weight: 500;
    text-align: center;
    font-size: 0.9rem;
  }
`;

// Products Coming Soon Section
const ProductsComingSoon = styled.section`
  margin-bottom: 120px;
  text-align: center;
  
  &.animate-in {
    animation: ${fadeInUp} 0.8s ease-out;
  }
`;

const SectionTitle = styled.h2`
  font-size: 3.5rem;
  
  color: #2c2c2c;
  margin-bottom: 30px;
  text-align: center;
  letter-spacing: -1.5px;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const ProductsDivider = styled.div`
  width: 80px;
  height: 3px;
  background: linear-gradient(90deg, #dc2626, #ef4444);
  border-radius: 2px;
  margin: 0 auto 35px;
`;

const ProductsMessage = styled.div`
  max-width: 600px;
  margin: 0 auto;
  background: linear-gradient(135deg, rgba(220, 38, 38, 0.03), rgba(239, 68, 68, 0.06));
  border: 1px solid rgba(220, 38, 38, 0.08);
  border-radius: 20px;
  padding: 50px 40px;
  
  .icon {
    width: 70px;
    height: 70px;
    background: linear-gradient(135deg, rgba(220, 38, 38, 0.08), rgba(239, 68, 68, 0.12));
    border-radius: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 25px;
    font-size: 1.8rem;
  }
  
  p {
    font-size: 1.1rem;
    color: #6c757d;
    line-height: 1.8;
    
    margin-bottom: 10px;
    
    &:last-child {
      margin-bottom: 0;
      font-size: 0.95rem;
      color: #9ca3af;
    }
  }
`;

// Contact Section
const ContactSection = styled.section`
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  padding: 100px 0;
  border-radius: 30px;
  text-align: center;
  
  &.animate-in {
    animation: ${scaleIn} 0.8s ease-out;
  }
`;

const ContactContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  
  h2 {
    font-size: 3rem;
    
    color: #2c2c2c;
    margin-bottom: 30px;
    letter-spacing: -1px;
  }
  
  p {
    font-size: 1.2rem;
    color: #6c757d;
    margin-bottom: 40px;
    line-height: 1.7;
    
  }
  
  .contact-info {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 40px;
    margin-top: 50px;
  }
  
  .contact-item {
    background: white;
    padding: 30px;
    border-radius: 15px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
    transition: transform 0.3s ease;
    
    &:hover {
      transform: translateY(-5px);
    }
    
    .icon {
      width: 60px;
      height: 60px;
      background: linear-gradient(135deg, #dc2626, #ef4444);
      border-radius: 15px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1.5rem;
      color: white;
      margin: 0 auto 20px;
    }
    
    h3 {
      font-size: 1.3rem;
      color: #2c2c2c;
      margin-bottom: 10px;
      font-weight: 500;
    }
    
    p {
      margin: 0;
      font-size: 1rem;
    }
  }
`;

const HalepFactory = () => {
  const { t } = useLanguage();
  const [animatedSections, setAnimatedSections] = useState(new Set());

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setAnimatedSections(prev => new Set(prev).add(entry.target.id));
          }
        });
      },
      { threshold: 0.1 }
    );

    const sections = document.querySelectorAll('[data-animate]');
    sections.forEach(section => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  return (
    <FactoryContainer>
      <Container>
        <PageTitle>{t('halep.title')}</PageTitle>
        <PageSubtitle>
          {t('halep.subtitle')}
        </PageSubtitle>

        <FactoryInfoSection 
          id="info-section" 
          data-animate
          className={animatedSections.has('info-section') ? 'animate-in' : ''}
        >
          <InfoContent className="info-content">
            <p>
              <span className="highlight">{t('halep.year')}</span> {t('halep.p1')}
              <span className="highlight"> {t('halep.p1capacity')}</span> {t('halep.p1end')}
            </p>
            <p>
              {t('halep.p2')}
            </p>
            <p>
              {t('halep.p3')}
            </p>
          </InfoContent>
          
          <InfoImages className="info-images">
            <ImageCard>
              <div className="placeholder">🏭</div>
              <div className="caption">{t('halep.overview')}</div>
            </ImageCard>
            <ImageCard>
              <div className="placeholder">🔧</div>
              <div className="caption">{t('halep.machinery')}</div>
            </ImageCard>
            <ImageCard>
              <div className="placeholder">📊</div>
              <div className="caption">{t('halep.controlCenter')}</div>
            </ImageCard>
            <ImageCard>
              <div className="placeholder">📦</div>
              <div className="caption">{t('halep.storage')}</div>
            </ImageCard>
          </InfoImages>
        </FactoryInfoSection>

        <ProductsComingSoon 
          id="products-section" 
          data-animate
          className={animatedSections.has('products-section') ? 'animate-in' : ''}
        >
          <SectionTitle>{t('factories.productsTitle')}</SectionTitle>
          <ProductsDivider />
          <ProductsMessage>
            <div className="icon">📦</div>
            <p>
              {t('factories.productsComingSoon')}
            </p>
            <p>
              {t('factories.productsContactHint')}
            </p>
          </ProductsMessage>
        </ProductsComingSoon>

        <ContactSection 
          id="contact-section" 
          data-animate
          className={animatedSections.has('contact-section') ? 'animate-in' : ''}
        >
          <ContactContent>
            <h2>{t('factories.contactTitle')}</h2>
            <p>
              {t('halep.contactText')}
            </p>
            
            <div className="contact-info">
              <div className="contact-item">
                <div className="icon">📍</div>
                <h3>{t('factories.address')}</h3>
                <p>{t('halep.addressL1')}<br/>{t('halep.addressL2')}</p>
              </div>
              
              <div className="contact-item">
                <div className="icon">📞</div>
                <h3>{t('factories.phone')}</h3>
                <p><bdi>0532 131 63 30</bdi><br/>{t('factories.factoryManagement')}</p>
              </div>
              
              <div className="contact-item">
                <div className="icon">✉️</div>
                <h3>{t('factories.email')}</h3>
                <p>info@dmrcimento.com<br/>{t('factories.technicalSupport')}</p>
              </div>
              
              <div className="contact-item">
                <div className="icon">🗺️</div>
                <h3>{t('factories.map')}</h3>
                <p><a href="https://maps.google.com/?q=Halep+Sanayi+Bölgesi,2.Cadde+No:8,Halep" target="_blank" rel="noopener noreferrer" style={{color: '#dc2626', textDecoration: 'none'}}>{t('factories.openInMaps')}</a></p>
              </div>
            </div>
          </ContactContent>
        </ContactSection>
      </Container>
    </FactoryContainer>
  );
};

export default HalepFactory;